/*
  custom.js — 41 & Change theme behavior add-ons
  Loaded (defer) from snippets/head.liquid. Keep bespoke JS here.

  ReCharge "Deliver Every" dropdown -> button group
  -------------------------------------------------
  ReCharge renders a native <select> for delivery frequency. We mirror it into
  the theme's .radio__button markup (same as the grind selector) so it inherits
  the theme styling + our green overrides, then keep the real <select> in sync
  (and fire change events) so ReCharge keeps functioning. Robust to the widget
  re-rendering via a debounced MutationObserver.
*/
(function () {
  /* -------------------------------------------------------------------------
     Capture ReCharge's real init() before it self-destructs.
     -------------------------------------------------------------------------
     ReCharge assigns window.Recharge (via et()) — boot uses et({init:br}) with
     the REAL init, then a later et({shop}) resets init to an "Init not
     implemented" stub. On product pages the page's own app block triggers that
     reset before our quick-view code runs, so reading window.Recharge.init then
     yields the stub. We install a setter NOW (custom.js runs before the body
     app-block module, per defer order) to grab the real init on first assign.
  ------------------------------------------------------------------------- */
  function isRealRcInit(fn) {
    return typeof fn === 'function' && fn.toString().indexOf('Init not implemented') === -1;
  }
  (function interceptRecharge() {
    if (window.__RC_REAL_INIT) return;
    try {
      var stored = Object.getOwnPropertyDescriptor(window, 'Recharge');
      if (stored && stored.value && isRealRcInit(stored.value.init)) {
        window.__RC_REAL_INIT = stored.value.init; // bundle already booted, init still real
        return;
      }
      var current = stored ? stored.value : undefined;
      Object.defineProperty(window, 'Recharge', {
        configurable: true,
        get: function () { return current; },
        set: function (v) {
          current = v;
          if (v && isRealRcInit(v.init) && !window.__RC_REAL_INIT) {
            window.__RC_REAL_INIT = v.init;
          }
        }
      });
    } catch (e) { /* non-configurable; fall back to captureRealInit() polling */ }
  })();

  function buildFor(select) {
    if (!select || !select.closest('.rc-widget')) return;
    if (select.dataset.rcButtons === '1') return;

    var options = Array.prototype.slice.call(select.options);
    if (!options.length) return;

    var parent = select.parentNode;
    if (!parent) return;

    // clear any stale button group we previously injected next to this select
    var stale = parent.querySelectorAll('.rc-frequency-buttons');
    for (var s = 0; s < stale.length; s++) stale[s].remove();

    select.dataset.rcButtons = '1';
    select.style.display = 'none';

    var name = 'rc-freq-' + (select.id || Math.floor(Math.random() * 1e9));

    var fieldset = document.createElement('div');
    fieldset.className = 'radio__fieldset rc-frequency-buttons';
    var buttons = document.createElement('div');
    buttons.className = 'radio__buttons';
    fieldset.appendChild(buttons);

    options.forEach(function (opt, i) {
      var span = document.createElement('span');
      span.className = 'radio__button';

      var input = document.createElement('input');
      input.type = 'radio';
      input.name = name;
      input.id = name + '-' + i;
      input.value = opt.value;
      input.checked = opt.selected;

      var label = document.createElement('label');
      label.setAttribute('for', input.id);
      var title = document.createElement('span');
      title.className = 'option-title';
      title.textContent = opt.textContent.trim();
      label.appendChild(title);

      input.addEventListener('change', function () {
        if (!input.checked) return;
        select.value = opt.value;
        select.dispatchEvent(new Event('input', { bubbles: true }));
        select.dispatchEvent(new Event('change', { bubbles: true }));
      });

      span.appendChild(input);
      span.appendChild(label);
      buttons.appendChild(span);
    });

    parent.insertBefore(fieldset, select.nextSibling);

    // if ReCharge changes the select programmatically, reflect it on the buttons
    select.addEventListener('change', function () {
      var safe = (window.CSS && CSS.escape) ? CSS.escape(select.value) : select.value;
      var current = fieldset.querySelector('input[value="' + safe + '"]');
      if (current && !current.checked) current.checked = true;
    });
  }

  function scan() {
    var selects = document.querySelectorAll('.rc-widget select');
    for (var i = 0; i < selects.length; i++) buildFor(selects[i]);
  }

  /* ---------------------------------------------------------------------------
     ReCharge subscription widget in the Quick View modal
     ---------------------------------------------------------------------------
     The widget is a Shopify @app block that only renders on product templates,
     so quick view (opened from home/collection pages) never has it.

     ReCharge's bundle deliberately makes window.Recharge.init SINGLE-USE: the
     real init (br) calls ar() -> et() which reassigns window.Recharge.init back
     to an "Init not implemented" stub. So the 2nd quick view would no-op.

     Fix: load the bundle ONCE ourselves and capture a reference to the real init
     BEFORE it is ever called (so it never gets reset out from under us). Then for
     each quick view we run ReCharge's own per-product inline config, rewritten to
     call our captured init instead of window.Recharge.init. Faithful (uses their
     exact config incl. selling plans + prices) and works for every product.
  --------------------------------------------------------------------------- */

  var rcBundlePromise = null;

  // Inject ReCharge's own widget stylesheet once (the app block ships it as a
  // <link> alongside the bundle; without it the Subscribe/One-Time block is
  // unstyled — no green bar, price jammed against the label).
  function ensureRechargeCss(href) {
    if (!href) return;
    if (href.indexOf('//') === 0) href = window.location.protocol + href;
    if (document.querySelector('link[data-rc-widget-css]')) return;
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute('data-rc-widget-css', '');
    document.head.appendChild(link);
  }

  function captureRealInit() {
    if (window.__RC_REAL_INIT) return true;
    // Fallback only: grab init directly if it still looks real (not the stub).
    if (window.Recharge && isRealRcInit(window.Recharge.init)) {
      window.__RC_REAL_INIT = window.Recharge.init;
      return true;
    }
    return false;
  }

  // Load the unified widget bundle once; resolve once the real init is captured.
  function ensureRechargeBundle(src) {
    if (rcBundlePromise) return rcBundlePromise;
    rcBundlePromise = new Promise(function (resolve, reject) {
      if (captureRealInit()) return resolve();
      // Pre-define onRechargeLoad so the bundle boot doesn't warn/spin; we never
      // rely on it (we drive init ourselves via the captured reference).
      if (typeof window.onRechargeLoad !== 'function') window.onRechargeLoad = function () {};

      var s = document.createElement('script');
      s.type = 'module';
      s.crossOrigin = 'anonymous';
      s.src = src;
      document.head.appendChild(s);

      var tries = 0;
      (function poll() {
        if (captureRealInit()) return resolve();
        if (tries++ > 120) return reject(new Error('ReCharge bundle did not initialize'));
        setTimeout(poll, 50);
      })();
    });
    return rcBundlePromise;
  }

  // Does this quick view's product have subscriptions? Read it synchronously
  // from the selected-variant JSON so non-subscription quick views are never
  // delayed by a loader / fetch.
  function quickviewHasSubscription(qv) {
    var el = qv.querySelector('[data-selected-variant]');
    if (!el) return false;
    try {
      var v = JSON.parse(el.textContent);
      return !!(v && v.selling_plan_allocations && v.selling_plan_allocations.length);
    } catch (e) { return false; }
  }

  function setQuickviewLoading(qv, on) {
    var content = qv.closest('.quickview-modal__content');
    if (content) content.classList.toggle('rc-qv-loading', !!on);
  }

  // Reveal once ReCharge has actually rendered (so the modal opens at final
  // size — no layout shift), with a timeout fallback so it never stays stuck.
  function revealWhenWidgetReady(qv, mountDiv) {
    var tries = 0;
    (function poll() {
      if (mountDiv.querySelector('.rc-widget') || tries++ > 80) {
        setQuickviewLoading(qv, false);
        return;
      }
      setTimeout(poll, 50);
    })();
  }

  function injectRechargeQuickview(qv) {
    if (!qv || qv.dataset.rcInjected === '1') return;
    var handle = qv.getAttribute('data-product-handle');
    if (!handle) return;
    qv.dataset.rcInjected = '1';

    if (!quickviewHasSubscription(qv)) return; // no widget -> no loader, no fetch

    setQuickviewLoading(qv, true);

    fetch('/products/' + encodeURIComponent(handle), { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var block = doc.querySelector('.recharge-subscription-widget');
        if (!block) { setQuickviewLoading(qv, false); return; } // no widget after all

        // bundle URL (real, hashed) + mount id + per-product inline config
        var bundleScript = block.querySelector('script[type="module"][src*="rc-widget"], script[src*="rc-widget"]');
        var bundleSrc = bundleScript && bundleScript.getAttribute('src');
        var cssLink = block.querySelector('link[rel="stylesheet"][href*="rc-widget"], link[rel="stylesheet"][href*="rc-"]');
        ensureRechargeCss(cssLink && cssLink.getAttribute('href'));
        var mount = block.querySelector('[id^="RechargeWidget_"]');
        var inline = null;
        var scripts = block.querySelectorAll('script:not([src])');
        for (var i = 0; i < scripts.length; i++) {
          if (/Recharge\.init/.test(scripts[i].textContent)) { inline = scripts[i].textContent; break; }
        }
        if (!bundleSrc || !mount || !inline) { setQuickviewLoading(qv, false); return; }
        if (bundleSrc.indexOf('//') === 0) bundleSrc = window.location.protocol + bundleSrc;

        // Place the mount above the variant picker (matches PDP order).
        var holder = document.createElement('div');
        holder.className = 'rc-quickview-widget';
        var mountDiv = document.createElement('div');
        mountDiv.id = mount.id;
        holder.appendChild(mountDiv);

        var wrapper = qv.querySelector('.product__info__wrapper') || qv.querySelector('.product__info') || qv;
        var variantBlock = wrapper.querySelector('variant-selects');
        var anchor = variantBlock ? (variantBlock.closest('.product__block') || variantBlock) : null;
        if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(holder, anchor);
        else wrapper.insertBefore(holder, wrapper.firstChild);

        ensureRechargeBundle(bundleSrc).then(function () {
          // Route ReCharge's own config through our captured real init so the
          // single-use stub reset can't break repeat opens.
          var code = inline.replace(/window\.Recharge\.init\s*\(/g, 'window.__RC_REAL_INIT(');
          var s = document.createElement('script');
          s.textContent = code;
          mountDiv.parentNode.appendChild(s);
          // ReCharge renders .rc-widget into the mount; scan() converts the
          // frequency <select> into themed buttons. Reveal once it's rendered.
          schedule();
          revealWhenWidgetReady(qv, mountDiv);
        }).catch(function () { setQuickviewLoading(qv, false); });
      })
      .catch(function () { setQuickviewLoading(qv, false); });
  }

  function scanQuickview() {
    var modals = document.querySelectorAll('.quickview-modal__content .quickview');
    for (var i = 0; i < modals.length; i++) injectRechargeQuickview(modals[i]);
  }

  /* -------------------------------------------------------------------------
     "Makes: X cups per shipment ($Y cost per cup)" (ported from old theme)
     -------------------------------------------------------------------------
     cups_per_bag is a per-variant metafield (custom.cups_per_bag) rendered into
     data-cups-map by buy-buttons.liquid. We compute cups = qty * cups_per_bag
     and cost_per_cup = (unit_price * qty) / cups from the displayed price, and
     refresh on variant/quantity changes. Works on PDP + quick view.
  ------------------------------------------------------------------------- */
  function cupsScope(el) {
    return el.closest('product-info') || el.closest('[data-section-type="product"]') ||
           el.closest('product-section') || document;
  }
  function readMoney(text) {
    var n = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : n;
  }
  function recomputeCups(container, map) {
    var scope = cupsScope(container);
    var priceEl = scope.querySelector('[data-product-price]');
    var idEl = scope.querySelector('.product-variant-id') || scope.querySelector('[name="id"]');
    var qtyEl = scope.querySelector('[data-quantity-input]');
    if (!priceEl || !idEl) { container.hidden = true; return; }

    var unit = readMoney(priceEl.textContent);
    var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 1) : 1;
    var cpb = map[String(idEl.value)];

    if (!cpb || !unit) { container.hidden = true; container.innerHTML = ''; return; }

    var cups = qty * cpb;
    var cost = ((unit * qty) / cups).toFixed(2);
    container.hidden = false;
    container.innerHTML = 'Makes:<br><span class="cups-made-info__count">' + cups +
      ' cups per shipment</span> <span class="cups-made-info__cost">($' + cost + ' cost per cup)</span>';
  }
  function scanCupsMade() {
    var els = document.querySelectorAll('[data-cups-made]');
    for (var i = 0; i < els.length; i++) {
      var container = els[i];
      if (container.dataset.cupsInit === '1') continue;
      container.dataset.cupsInit = '1';
      var map;
      try { map = JSON.parse(container.getAttribute('data-cups-map') || '{}'); } catch (e) { map = {}; }
      (function (container, map) {
        var scope = cupsScope(container);
        var run = function () { recomputeCups(container, map); };
        run();
        scope.addEventListener('change', run);
        scope.addEventListener('click', function (e) {
          if (e.target.closest('.quantity__button, [data-quantity-button]')) setTimeout(run, 60);
        });
        var priceEl = scope.querySelector('[data-product-price]');
        if (priceEl) new MutationObserver(run).observe(priceEl, { childList: true, subtree: true, characterData: true });
      })(container, map);
    }
  }

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    var raf = window.requestAnimationFrame || function (cb) { return window.setTimeout(cb, 16); };
    raf(function () { scheduled = false; scan(); scanQuickview(); scanCupsMade(); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(); scanQuickview(); scanCupsMade(); });
  } else {
    scan();
    scanQuickview();
    scanCupsMade();
  }

  new MutationObserver(schedule).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();

/* ===========================================================================
   "Current Young Life Give Back: $X per year!" — ReCharge bundle builder
   (ported from old/snippets/andy-recharge-bundle-modifier.liquid; benefactor
   parts dropped per scope). Runs only where the ReCharge bundle widget exists
   (build-your-own-subscription). Per-order charity = sum(custom.total_charity_amount
   * qty) across selected items (from the 41change metafields API); yearly =
   (365 / shipping_days) * per_order. Injected into the widget's [role=status].
   Note: the metafields API only allows CORS from https://www.41change.com, so
   the live $ amount computes on production; in preview it shows $0.00.
=========================================================================== */
(function bundleGiveback() {
  var ICON = '<svg class="give-back-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M12 21s-7.5-4.6-10-9.2C.6 9 1.7 5.6 4.8 4.9c1.9-.4 3.7.4 4.7 1.9.5.7 1.5.7 2 0 1-1.5 2.8-2.3 4.7-1.9 3.1.7 4.2 4.1 2.8 6.9C19.5 16.4 12 21 12 21z"/>' +
    '</svg>';

  var lastItemSig = null;
  var lastPerOrder = 0;

  function num(t) { var n = parseFloat(String(t || '').replace(/[^0-9.]/g, '')); return isNaN(n) ? 0 : n; }

  function shippingDays() {
    var sel = document.querySelector('.rb-frequency-select') || document.querySelector('.rb-select.rb-frequency-select');
    if (!sel || sel.selectedIndex < 0) return 30;
    var opt = sel.options[sel.selectedIndex];
    var m = (opt && opt.textContent || '').match(/\d+/);
    return m ? Number(m[0]) : 30;
  }

  function selectedItems() {
    var items = [];
    document.querySelectorAll('.rb-item').forEach(function (it) {
      var vsel = it.querySelector('.rb-variant-selector');
      var qtyEl = it.querySelector('.rb-input');
      var vid = vsel ? vsel.value : null;
      var qty = qtyEl ? (parseInt(qtyEl.value, 10) || 0) : 0;
      if (vid && qty > 0) items.push({ id: vid, qty: qty });
    });
    return items;
  }

  function bundleStatus() {
    // Inject inside the bundle's sticky content card (.rb-bg) within the status
    // bar — NOT the (first, hidden) predictive-search [role=status].
    return document.querySelector('.status-bar-sign-up .rb-bg') ||
           document.querySelector('.rb-bg.rb-sticky-content') ||
           document.querySelector('#recharge-bundles .rb-bg') ||
           document.querySelector('.status-bar-sign-up');
  }
  function ensureMessageEl() {
    var status = bundleStatus();
    if (!status) return null;
    var msg = status.querySelector('.shipping-message');
    if (!msg) {
      // clean up any stray message injected elsewhere (e.g. the hidden search status)
      var stray = document.querySelector('.shipping-message');
      if (stray) stray.remove();
      msg = document.createElement('div');
      msg.className = 'shipping-message';
      status.prepend(msg);
    }
    return msg;
  }

  function yearlyFrom(perOrder) {
    var days = shippingDays();
    if (!days || days <= 0) return '0.00';
    var yearly = (365 / days) * perOrder;
    return (Math.round(yearly * 100) / 100).toFixed(2);
  }

  function render(perOrder) {
    var msg = ensureMessageEl();
    if (!msg) return;
    var yearly = yearlyFrom(perOrder);
    // Track the rendered value via a data attribute — comparing innerHTML fails
    // because the browser re-serializes the SVG, causing an endless repaint loop.
    if (msg.dataset.yearly === yearly && msg.firstChild) return;
    msg.dataset.yearly = yearly;
    msg.innerHTML = ICON + '<span>Current Young Life Give Back: $' + yearly + ' per year!</span>';
  }

  function tick() {
    if (!document.querySelector('.rb-item')) return; // bundle widget not present/loaded
    var items = selectedItems();
    // keep the line current (e.g. frequency changed) using the last known per-order amount
    render(lastPerOrder);

    var sig = JSON.stringify(items);
    if (sig === lastItemSig) return; // selection unchanged -> no refetch
    lastItemSig = sig;

    if (!items.length) { lastPerOrder = 0; render(0); return; }

    var ids = items.map(function (i) { return 'gid://shopify/ProductVariant/' + i.id; }).join(',');
    fetch('https://prod.api.41change.com/api/product_variations_metafields?ids=' + encodeURIComponent(ids) + '&loc=cart_details')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        var perOrder = 0;
        var nodes = (d && d.data && d.data.nodes) || [];
        nodes.forEach(function (n) {
          var kv = {};
          ((n.metafields && n.metafields.edges) || []).forEach(function (e) { kv[e.node.key] = e.node.value; });
          var amt = parseFloat(kv['total_charity_amount']) || 0;
          var qty = 0;
          items.forEach(function (it) { if ('gid://shopify/ProductVariant/' + it.id === n.id) qty = it.qty; });
          perOrder += amt * qty;
        });
        lastPerOrder = perOrder;
        render(perOrder);
      })
      .catch(function () { /* CORS in preview / offline — keep last value */ });
  }

  setInterval(tick, 500);
})();

/* ===========================================================================
   Benefactor: cart attribute sync + "your purchase supports" banner
   ---------------------------------------------------------------------------
   A benefactor slug link (handled in sections/404.liquid) stores the resolved
   benefactor in localStorage. Here, site-wide, we:
     1) mirror it into Shopify cart attributes so it rides onto the order as
        note_attributes (Order Version=v2, Benefactor ID, Benefactor Name) —
        the post-checkout webhook reads these and recomputes donation amounts.
     2) show a banner naming the active benefactor.
   No JWT: amounts are computed server-side from product metafields, and the
   benefactor gid is already public — matches the prior trust model.
=========================================================================== */
(function benefactorSync() {
  var YLN_ID = 'gid://shopify/Metaobject/19656278188'; // Young Life Nicaragua (default)

  function getBenefactor() {
    try { return JSON.parse(localStorage.getItem('benefactor')); } catch (e) { return null; }
  }

  function desiredAttrs(b) {
    return {
      'Order Version': 'v2',
      'Benefactor ID': b.id || '',
      'Benefactor Name': b.displayName || ''
    };
  }

  function syncCartAttributes(b) {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var want = desiredAttrs(b);
        var have = cart.attributes || {};
        var needs = false;
        for (var k in want) { if (have[k] !== want[k]) { needs = true; break; } }
        if (!needs) return;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: want })
        });
      })
      .catch(function () { /* non-fatal */ });
  }

  function renderBanner(b) {
    if (!b || !b.displayName) return;
    // The selector page states the active benefactor itself — the site-wide
    // banner is redundant noise there.
    if (document.querySelector('.benefactor-select')) return;
    if (document.querySelector('.benefactor-banner')) {
      var span = document.querySelector('.benefactor-banner__name');
      if (span && span.textContent !== b.displayName) span.textContent = b.displayName;
      return;
    }
    var main = document.getElementById('MainContent') || document.querySelector('main');
    if (!main) return;
    var bar = document.createElement('div');
    bar.className = 'benefactor-banner';
    bar.innerHTML = 'Your purchase supports: <span class="benefactor-banner__name"></span>';
    bar.querySelector('.benefactor-banner__name').textContent = b.displayName;
    main.insertBefore(bar, main.firstChild);
  }

  function run() {
    var b = getBenefactor();
    // No explicit choice on this device? Fall back to the customer's saved
    // default benefactor (customer.metafields.custom.default_benefactor,
    // injected by snippets/head.liquid when logged in).
    if ((!b || !b.id) && window.__customerDefaultBenefactor && window.__customerDefaultBenefactor.id) {
      b = window.__customerDefaultBenefactor;
    }
    if (!b || !b.id) return;
    syncCartAttributes(b);
    renderBanner(b);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();

/* ===========================================================================
   Cart give-back summary (drawer + cart page)
   Fills [data-cart-giveback] (snippets/site-cart.liquid) with who this
   order's give-back goes to and the split amounts, computed from the cart's
   variants x their charity metafields. Same formula as the middleware and
   the selector page: a chosen benefactor receives Large-Small and Young Life
   Nicaragua keeps Small; YLN itself (or no choice) receives all of Large.
   Re-renders after every ajax cart mutation via a fetch patch.
=========================================================================== */
(function cartGiveback() {
  var YLN_ID = 'gid://shopify/Metaobject/19656278188';
  var YLN_NAME = 'Young Life Nicaragua';
  var API = 'https://prod.api.41change.com';
  var ICON = '<svg class="give-back-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" d="M12 21s-7.5-4.6-10-9.2C.6 9 1.7 5.6 4.8 4.9c1.9-.4 3.7.4 4.7 1.9.5.7 1.5.7 2 0 1-1.5 2.8-2.3 4.7-1.9 3.1.7 4.2 4.1 2.8 6.9C19.5 16.4 12 21 12 21z"/>' +
    '</svg>';

  var amounts = {}; // variant id -> {large, small}, cached for the session
  var debounceTimer = null;

  function escapeHtml(s) {
    var d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
  }

  function money(n) { return '$' + Number(n).toFixed(2); }

  function currentBenefactor() {
    try {
      var b = JSON.parse(localStorage.getItem('benefactor'));
      if (b && b.id) return b;
    } catch (e) { /* fall through */ }
    if (window.__customerDefaultBenefactor && window.__customerDefaultBenefactor.id) {
      return window.__customerDefaultBenefactor;
    }
    return { id: YLN_ID, displayName: YLN_NAME };
  }

  function fetchAmounts(variantIds) {
    var missing = variantIds.filter(function (v) { return !(v in amounts); });
    if (!missing.length) return Promise.resolve();
    var ids = missing.map(function (v) { return 'gid://shopify/ProductVariant/' + v; }).join(',');
    return fetch(API + '/api/product_variations_metafields?ids=' + encodeURIComponent(ids) + '&loc=cart_details')
      .then(function (r) { return r.json(); })
      .then(function (d) {
        (((d || {}).data || {}).nodes || []).forEach(function (n) {
          if (!n || !n.id) return;
          var kv = {};
          ((n.metafields && n.metafields.edges) || []).forEach(function (e) { kv[e.node.key] = e.node.value; });
          amounts[String(n.id).split('/').pop()] = {
            large: parseFloat(kv['total_charity_amount']) || 0,
            small: parseFloat(kv['total_charity_amount_for_other_beneficiary1']) || 0
          };
        });
        // Anything the API didn't return contributes nothing (e.g. merch)
        missing.forEach(function (v) { if (!(v in amounts)) amounts[v] = { large: 0, small: 0 }; });
      });
  }

  function render(html) {
    document.querySelectorAll('[data-cart-giveback]').forEach(function (el) {
      if (html) {
        el.innerHTML = html;
        el.hidden = false;
      } else {
        el.hidden = true;
        el.innerHTML = '';
      }
    });
  }

  function recompute() {
    if (!document.querySelector('[data-cart-giveback]')) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var items = cart.items || [];
        if (!items.length) { render(null); return; }
        var variantIds = items.map(function (i) { return String(i.variant_id); });
        return fetchAmounts(variantIds).then(function () {
          var large = 0, small = 0;
          items.forEach(function (i) {
            var a = amounts[String(i.variant_id)] || { large: 0, small: 0 };
            large += a.large * i.quantity;
            small += a.small * i.quantity;
          });
          if (large <= 0) { render(null); return; }

          var b = currentBenefactor();
          var isYln = !b.id || b.id === YLN_ID;
          var link = '/pages/select-benefactor?return_to=' + encodeURIComponent(window.location.href);
          var text;
          if (isYln) {
            text = 'This order gives back <strong>' + money(large) + '</strong> to <strong>' + escapeHtml(YLN_NAME) + '</strong>.';
          } else {
            text = 'This order gives back <strong>' + money(large - small) + '</strong> to <strong>' + escapeHtml(b.displayName) + '</strong>' +
              ' and <strong>' + money(small) + '</strong> to <strong>' + escapeHtml(YLN_NAME) + '</strong>.';
          }
          render(ICON + '<span class="cart-giveback__text">' + text +
            ' <a class="cart-giveback__change" href="' + link + '">Change</a></span>');
        });
      })
      .catch(function () { /* non-fatal */ });
  }

  // Recompute after any ajax cart mutation (add/change/update/clear)
  var originalFetch = window.fetch;
  window.fetch = function () {
    var url = typeof arguments[0] === 'string' ? arguments[0] : ((arguments[0] && arguments[0].url) || '');
    var result = originalFetch.apply(this, arguments);
    if (/\/cart\/(add|change|update|clear)/.test(url)) {
      result.then(function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(recompute, 400);
      }).catch(function () { /* non-fatal */ });
    }
    return result;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', recompute);
  } else {
    recompute();
  }
})();
